(ns pfo.api
  (:require
   [ajax.core]
   [alandipert.storage-atom :refer [local-storage]]
   [promesa.core :as p]
   [pfo.flag :refer [enabled]]
   [pfo.flash :refer [set-flash]]
   [pfo.valid :as valid]
   [pfo.util :as util]
   [pfo.state :as state]
   [pfo.time :as time]
   [goog.string :refer [startsWith format]]
   [reagent.core :as reagent :refer [atom]]
   [cljs-time.core :as time-core]
   [cljs-time.format :as time-format]
   [clojure.string :as str]))

(declare set-auth-info get-auth-info report-error)

(defn default-success [response] (.info js/console (str "Success: " response)))

(defn default-fail [response]
  (.error js/console (str "Fail: " response))

  ;; Flip to :logged-out only from :logged-in — a stray 401 from a background
  ;; request holding a stale token must not wipe the :mfa/:mfa-setup screens
  ;; back to the login form (#3058).
  (when (and (= 401 (:status response))
             (= :logged-in (get-auth-info :auth-state)))
    (set-auth-info :auth-state :logged-out)))

(def auth-info (local-storage (atom {}) :authinfo))

(def local-auth-info (atom {}))

(defn auth-info-atom [] (if @state/use-local-auth-state
                          local-auth-info
                          auth-info))

(defn set-auth-info [key value]
  (when (= [key value] [:auth-state :logged-out]) (reset! state/parent-id-filter-map {}))
  (swap! (auth-info-atom) assoc key value))

(defn delete-auth-info [key]
  (swap! (auth-info-atom) dissoc key))

(defn get-auth-info [key] (get @(auth-info-atom) key))

(defn get-auth-token [] (get-in @(auth-info-atom) [ :data :token ]))

(add-watch (auth-info-atom) :auth-info (fn [_ _ olds news]
                                  (when (and (= :logged-out (get news :auth-state)) (not= :logged-out (get olds :auth-state)))
                                    (when (startsWith (.-hash (.-location js/window)) "#/admin")
                                       (util/set-location "/#/")))))


(defn auth-headers []
  (if (empty? (get-auth-token))
    {}
    {"Authorization" (str "PFO " (get-auth-token))}))

(defn flow-to-str [flow]
  (str (:location flow) " (" (time-format/unparse (time-format/formatters :date-time) (:time flow)) ")"))

(defn info-headers []
  {:X-Flow (clojure.string/join " -> " (map flow-to-str @state/nav-flow))
   :X-UI-ID state/ui-id
   :X-UI-Version (aget js/window "VERSION")})

(defn api-url [url] (str js/API_BASE_URL url))

;; Performs API call
(defn ajax
  ([url options success fail]
   (let [opts (merge {
                      :method :get
                      :uri (api-url url)
                      :handler (fn [[ok response]]
                                 (if ok
                                   ((or success default-success) response)
                                   ((or fail default-fail) response)))
                      :format (ajax.core/url-request-format)
                      :response-format (ajax.core/json-response-format {:keywords? true})
                      :headers (merge (auth-headers) (info-headers))} options)]
     (ajax.core/ajax-request opts)))
  ([url options success] (ajax url options success nil))
  ([url options] (ajax url options nil nil))
  ([url] (ajax url {} nil nil)))

(def redirect-code js/REDIRECT_CODE)

(defn raw-response-format [] (ajax.core/ring-response-format {:format (ajax.core/raw-response-format)}))

(defn ajax-request [{disable-loading-counter :disable-loadin-counter
                     :as opts} resolve reject]
  (ajax.core/ajax-request
    (merge {:handler (fn [[ok response]]
                       (when-not disable-loading-counter
                         (swap! state/loading-count
                           ;; NOTE 2024-09-27:
                           ;; For reason currently unknown, the counter is sometimes decreased without prior increase.
                           ;; This caused the spinner to stop appearing at all.
                           ;; TODO: investigate
                           (fn [old-count]
                             (if (pos? old-count) (dec old-count) 0))))
                       (if (= redirect-code (:status response))
                         (ajax-request (assoc opts :uri (get-in response [:response :body :location])) resolve reject)
                         (if ok
                           (do
                             (when-let [toffsetstr (get-in response [:headers "x-toffset"])]
                               (reset! time/toffset (time-core/millis (util/str->int toffsetstr))))
                             (if (:response-with-headers opts)
                               (resolve {:body (:body response)
                                         :headers (:headers response)})
                               (resolve (:body response))))
                           (do
                             (if-not (:skip-default-fail opts)
                               (default-fail response))
                             (reject response)))))}
           opts)))

;; Performs API call, equivalent to ajax function but returns a promise
(defn ajaxp [url options]
  (let [disable-loading-counter (:disable-loadin-counter options)
        disable-timestamp (:disable-timestamp options)
        opts (merge {:method :get
                     :uri (str js/API_BASE_URL url)
                     :format (ajax.core/url-request-format)
                    :response-format (ajax.core/ring-response-format {:format (ajax.core/json-response-format {:keywords? true})})}
                    options
                    {:headers (merge
                                (:headers options)
                                (auth-headers)
                                (info-headers))}
                    {:params (merge
                               (when-not disable-timestamp
                                 {:_ts (.getTime (js/Date.))})
                               (:params options))})]
    (p/promise (fn [resolve reject]
                 (when-not disable-loading-counter (swap! state/loading-count inc))
                 (ajax-request opts resolve reject)))))

(defn handle-network-error [resp]
  (let [code-str (when (enabled "display-error-code")
                   (str
                     "1" ; schema version
                     (time-format/unparse (time-format/formatter "yyMMddhhmmss") (time/now)) ; UTC time
                     (if (:status resp)
                       "1"  ; network layer error
                       "0") ; other error
                     (if (and (not (nil? (:status resp))) (not= 0 (:status resp)))
                       "1"  ; HTTP error
                       "0") ; other network error
                     (case (:failure resp)
                       :error   "1" ; cljs-ajax: server error
                       :parse   "2" ; cljs-ajax: response parse error
                       :aborted "3" ; cljs-ajax: client aborted
                       :timeout "4" ; cljs-ajax: request timeout
                       "0")         ; other cljs-ajax status or none
                     (format "%03d" (or (:status resp) 0))
                     (if (get-in resp [:response :body :iperr])
                       "1"    ; HTTP error response comes from iPaladin
                       "0")))]
    (set-flash
      :error
      (if code-str
        (str "Network error, please try again later (code: " code-str ")")
        "Network error, please try again later."))
    (report-error :async code-str (ex-message resp) nil)))

(defn handle-permission-error [response]
  (set-flash :error "You don't have permission to perform this action. If you think this is an error, please contact support."))

(defn handle-flash-errors [response errors]
  (let [error-messages (filter identity (vals errors))]
    (set-flash :error (if (= 1 (count error-messages)) (first error-messages)
                          (str "Errors: " (str/join " " (sort error-messages)))))))

(defn handle-error-response 
  ([errors-atom {:keys [status response] :as resp} flash-error-fields]
   (if (and (= 422 status) errors-atom)
     (do
       (reset! errors-atom (valid/field-errors (:body response)))
       (when (seq (clojure.set/intersection (set (keys @errors-atom)) (set flash-error-fields)))
         (handle-flash-errors response @errors-atom)))
   (case status
     403 (handle-permission-error response)
     401 nil
     404 (set-flash :error "Object no longer exists.")
     409 (set-flash :error [:span "iPaladin has made some changes. Please " [:a {:href "javascript:void()"
                                                                                 :on-click (fn [] (.reload (.-location js/window) true))} "reload"]
                            " the page before performing this action."] )
     400 (if (:type (:body response))  (set-flash :error (:message (:body response) )) (handle-network-error resp))
     (handle-network-error resp))))
  ([errors-atom response] (handle-error-response errors-atom response nil)))

(defn process-error
  "Puts validation error messages into the specified hash atom if the response
  code 422. Otherwise, invoke the general network error handler"
  ([errors-atom prom]
   (p/error #(handle-error-response errors-atom % nil) prom))
  ([errors-atom flash-error-fields prom]
   (p/error #(handle-error-response errors-atom % flash-error-fields) prom))
  ([prom] (process-error nil prom)))

(defn report-errors-call [type code description location]
  (ajaxp (str "/client_errors/")
         {:method :post
          :params (util/remove-nil-values {:type type
                                           :code code
                                           :description description
                                           :location location})}))

(defn report-error [type code description location] 
  (when (enabled "client-error-logging")
         (->> (report-errors-call type code description location)
              (p/map (fn [response] (prn response)))
              (p/error (fn [err] (prn err))))))
